import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Package,
  Wallet,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  ShoppingCart,
  ArrowRight,
  Heart,
  QrCode,
  Check,
  X,
  ThumbsUp,
  Coins,
  MessageSquare
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";

const StatCard = ({ icon: Icon, title, value, subtitle, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay }}
  >
    <Card className="border-slate-200 hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className={`text-3xl font-heading font-bold mt-1 ${color}`}>
              {value}
            </p>
            {subtitle && (
              <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`w-12 h-12 rounded-md flex items-center justify-center ${color.replace('text-', 'bg-').replace('600', '100')}`}>
            <Icon className={`w-6 h-6 ${color}`} strokeWidth={1.5} />
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

const ActivityItem = ({ item, type }) => {
  const isRefill = type === "refill";
  const Icon = isRefill ? RefreshCw : ShoppingCart;
  
  return (
    <div className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0">
      <div className={`w-10 h-10 rounded-md flex items-center justify-center ${isRefill ? 'bg-emerald-100' : 'bg-rose-100'}`}>
        <Icon className={`w-5 h-5 ${isRefill ? 'text-emerald-600' : 'text-rose-600'}`} strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 truncate">
          {item.product_name}
        </p>
        <p className="text-sm text-slate-500">
          {isRefill ? `Fach ${item.slot_id} · +${item.quantity}` : `${item.quantity}x · ${item.total?.toFixed(2)}€`}
        </p>
      </div>
      <span className="text-xs text-slate-400 font-mono">
        {new Date(item.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
      </span>
    </div>
  );
};

const WishItem = ({ wish, onUpdate }) => {
  const handleApprove = async () => {
    try {
      await axios.put(`${API}/wishes/${wish.id}`, { status: "approved" });
      toast.success("Wunsch genehmigt");
      onUpdate();
    } catch (err) {
      toast.error("Fehler");
    }
  };

  const handleReject = async () => {
    try {
      await axios.delete(`${API}/wishes/${wish.id}`);
      toast.success("Wunsch entfernt");
      onUpdate();
    } catch (err) {
      toast.error("Fehler");
    }
  };

  return (
    <div className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
      <div className="w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center">
        <Heart className="w-4 h-4 text-rose-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 truncate text-sm">{wish.product_name}</p>
        {wish.comment && <p className="text-xs text-slate-500 truncate">{wish.comment}</p>}
      </div>
      <div className="flex items-center gap-1">
        <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full font-medium">
          {wish.votes}x
        </span>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" onClick={handleApprove}>
          <Check className="w-4 h-4" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={handleReject}>
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const QRCodeDialog = () => {
  // Use production URL instead of localhost
  const baseUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || 'https://snackomat.luenefire.de';
  const wishUrl = `${baseUrl}/wunsch`;
  const problemUrl = `${baseUrl}/problem`;
  
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="qr-code-btn">
          <QrCode className="w-4 h-4 mr-2" />
          QR-Codes
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[95vw] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>QR-Codes zum Ausdrucken</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-4">
          {/* Wish QR */}
          <div className="flex flex-col items-center">
            <div className="bg-white p-2 rounded-lg shadow-inner border">
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(wishUrl)}`}
                alt="Wunsch QR Code"
                className="w-28 h-28 sm:w-32 sm:h-32"
              />
            </div>
            <p className="text-xs font-medium text-slate-700 mt-2 text-center">
              Produktwunsch
            </p>
            <p className="text-[10px] text-slate-400 text-center">
              {wishUrl}
            </p>
          </div>
          
          {/* Problem QR */}
          <div className="flex flex-col items-center">
            <div className="bg-white p-2 rounded-lg shadow-inner border border-amber-200">
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(problemUrl)}`}
                alt="Problem QR Code"
                className="w-28 h-28 sm:w-32 sm:h-32"
              />
            </div>
            <p className="text-xs font-medium text-amber-700 mt-2 text-center">
              Problem melden
            </p>
            <p className="text-[10px] text-slate-400 text-center">
              {problemUrl}
            </p>
          </div>
        </div>
        <p className="text-xs text-slate-500 text-center">
          Diese QR-Codes können Sie ausdrucken und am Automaten anbringen
        </p>
      </DialogContent>
    </Dialog>
  );
};

const ProblemItem = ({ problem, onResolve, onReject }) => {
  const typeLabels = {
    money_lost: { label: "Geld weg", color: "bg-amber-100 text-amber-700" },
    empty_slot: { label: "Fach leer", color: "bg-rose-100 text-rose-700" },
    other: { label: "Sonstiges", color: "bg-blue-100 text-blue-700" }
  };
  
  const typeInfo = typeLabels[problem.problem_type] || typeLabels.other;
  
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-0">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
          <span className="text-sm font-medium truncate">{problem.reporter_name}</span>
        </div>
        <p className="text-xs text-slate-500 truncate mt-1">
          {problem.amount > 0 && `${problem.amount.toFixed(2)}€ • `}
          {problem.slot_id && `Fach ${problem.slot_id} • `}
          {problem.description || "Keine Details"}
        </p>
      </div>
      <div className="flex items-center gap-1 ml-2">
        <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" onClick={() => onResolve(problem.id)}>
          <Check className="w-4 h-4" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => onReject(problem.id)}>
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [wishes, setWishes] = useState([]);
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, wishesRes, problemsRes] = await Promise.all([
        axios.get(`${API}/reports/dashboard`),
        axios.get(`${API}/wishes`),
        axios.get(`${API}/problem-reports?status=open`)
      ]);
      setStats(statsRes.data);
      setWishes(wishesRes.data.filter(w => w.status === "open"));
      setProblems(problemsRes.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const handleResolveProblem = async (id) => {
    try {
      await axios.put(`${API}/problem-reports/${id}/resolve`);
      toast.success("Problem als erledigt markiert");
      fetchData();
    } catch (err) {
      toast.error("Fehler");
    }
  };

  const handleRejectProblem = async (id) => {
    try {
      await axios.put(`${API}/problem-reports/${id}/reject`);
      toast.success("Problem abgelehnt");
      fetchData();
    } catch (err) {
      toast.error("Fehler");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
          Dashboard
        </h1>
        <p className="text-slate-500 mt-1">
          Übersicht über den Snackautomaten
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Wallet}
          title="Kassenbestand"
          value={`${stats?.cash_balance?.toFixed(2) || '0.00'}€`}
          subtitle={`+${stats?.revenue_today?.toFixed(2) || '0.00'}€ heute`}
          color="text-emerald-600"
          delay={0}
        />
        <StatCard
          icon={Package}
          title="Produkte"
          value={stats?.products_count || 0}
          subtitle={`${stats?.filled_compartments || 0} Fächer belegt`}
          color="text-blue-600"
          delay={0.1}
        />
        <StatCard
          icon={AlertTriangle}
          title="Niedriger Bestand"
          value={stats?.low_stock_count || 0}
          subtitle="Fächer nachfüllen"
          color="text-amber-600"
          delay={0.2}
        />
        <StatCard
          icon={TrendingUp}
          title="Umsatz heute"
          value={`${stats?.revenue_today?.toFixed(2) || '0.00'}€`}
          subtitle={`${stats?.sales_count_today || 0} Verkäufe`}
          color="text-rose-600"
          delay={0.3}
        />
      </div>

      {/* Activity Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Refills */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-heading">
                Letzte Auffüllungen
              </CardTitle>
              <Link to="/auffuellen">
                <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700">
                  Alle <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {stats?.recent_refills?.length > 0 ? (
                <div>
                  {stats.recent_refills.map((item) => (
                    <ActivityItem key={item.id} item={item} type="refill" />
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">
                  Noch keine Auffüllungen
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Recent Sales */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
        >
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-heading">
                Letzte Verkäufe
              </CardTitle>
              <Link to="/berichte">
                <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700">
                  Alle <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {stats?.recent_sales?.length > 0 ? (
                <div>
                  {stats.recent_sales.map((item) => (
                    <ActivityItem key={item.id} item={item} type="sale" />
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">
                  Noch keine Verkäufe
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Product Wishes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.55 }}
      >
        <Card className="border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Heart className="w-5 h-5 text-rose-500" />
              Produktwünsche
              {wishes.length > 0 && (
                <span className="bg-rose-100 text-rose-600 text-xs px-2 py-0.5 rounded-full">
                  {wishes.length}
                </span>
              )}
            </CardTitle>
            <QRCodeDialog />
          </CardHeader>
          <CardContent>
            {wishes.length > 0 ? (
              <div className="max-h-64 overflow-y-auto">
                {wishes.map((wish) => (
                  <WishItem key={wish.id} wish={wish} onUpdate={fetchData} />
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-6">
                Keine offenen Wünsche
              </p>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Problem Reports */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.6 }}
      >
        <Card className="border-slate-200 border-amber-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Problemmeldungen
              {problems.length > 0 && (
                <span className="bg-amber-100 text-amber-600 text-xs px-2 py-0.5 rounded-full">
                  {problems.length}
                </span>
              )}
            </CardTitle>
            <QRCodeDialog />
          </CardHeader>
          <CardContent>
            {problems.length > 0 ? (
              <div className="max-h-64 overflow-y-auto">
                {problems.map((problem) => (
                  <ProblemItem 
                    key={problem.id} 
                    problem={problem} 
                    onResolve={handleResolveProblem}
                    onReject={handleRejectProblem}
                  />
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-6">
                Keine offenen Meldungen
              </p>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.65 }}
      >
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading">
              Schnellzugriff
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Link to="/auffuellen">
                <Button className="bg-rose-600 hover:bg-rose-700 btn-hover" data-testid="quick-refill-btn">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Auffüllen
                </Button>
              </Link>
              <Link to="/automat">
                <Button variant="outline" className="btn-hover" data-testid="quick-automat-btn">
                  Automat anzeigen
                </Button>
              </Link>
              <Link to="/kasse">
                <Button variant="outline" className="btn-hover" data-testid="quick-cash-btn">
                  <Wallet className="w-4 h-4 mr-2" />
                  Kasse
                </Button>
              </Link>
              <Link to="/produkte">
                <Button variant="outline" className="btn-hover" data-testid="quick-products-btn">
                  <Package className="w-4 h-4 mr-2" />
                  Produkte verwalten
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default DashboardPage;
